// User-facing key redemption page — user enters activation key to unlock a tool
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function RedeemKey() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [activations, setActivations] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/activation-keys/my-activations');
      setActivations(r.data.activations || []);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async () => {
    if (!code.trim()) { toast.error('Enter your activation key'); return; }
    setBusy(true);
    try {
      const r = await api.post('/activation-keys/redeem', { code: code.trim().toUpperCase() });
      toast.success(`🎉 ${r.data.tool_slug} unlocked for ${r.data.validity_days} days!`);
      setCode('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid key'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 text-white p-4">
      <div className="max-w-2xl mx-auto space-y-4 pt-10" data-testid="redeem-key-page">
        <div className="text-center space-y-1">
          <div className="text-5xl">🎁</div>
          <h1 className="text-3xl font-black">Redeem Activation Key</h1>
          <p className="text-sm opacity-80">Enter your key to unlock the full plan of a tool</p>
        </div>

        <div className="rounded-2xl bg-slate-900/70 border border-yellow-400/40 p-4 space-y-3">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && redeem()}
                 placeholder="AN-XXXX-XXXX-XXXX" data-testid="redeem-key-input"
                 className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-lg font-mono uppercase text-center tracking-widest" />
          <button onClick={redeem} disabled={busy} data-testid="redeem-key-submit"
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 font-black text-base flex items-center justify-center gap-2 disabled:opacity-50">
            <KeyRound size={18} /> {busy ? 'Redeeming…' : 'Unlock Tool'}
          </button>
        </div>

        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-4">
          <div className="text-sm font-black mb-2">My Active Tools</div>
          {activations.length === 0 && <div className="text-center py-6 opacity-60 text-xs">No active tools yet — redeem your first key!</div>}
          <div className="space-y-1.5">
            {activations.map((a, i) => (
              <div key={i} className="p-2 rounded-lg bg-slate-800/60 flex items-center gap-2" data-testid={`activation-row-${a.tool_slug}`}>
                <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black">{a.tool_slug}</div>
                  <div className="text-[10px] opacity-70 flex items-center gap-1">
                    <Clock size={9} /> Expires {a.expires_at ? new Date(a.expires_at).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
                {a.code_used && <div className="text-[9px] font-mono opacity-60">{a.code_used}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
